import type { FC } from 'react';
import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import useRulePageMode from '../../hooks/useRulePageMode';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import type { OptionValue } from '../../../../types/shared';
import { Box, TextField, Typography } from '@mui/material';
import type { ConditionVariantProps } from '../../types';

export const CriticalValueField: FC<Omit<ConditionVariantProps, 'field'>> = observer(
  ({ indexCondition, indexTask, indexVariant }) => {
    const { isRead } = useRulePageMode();
    const isVariant = typeof indexVariant === 'number';
    const tasksFormStore = useCurrentTasksFormStore();

    const handleChangeCriticalValue = useCallback(
      (value: OptionValue) => {
        if (isVariant) {
          tasksFormStore.setVariantOnTaskConditionMetric(
            indexTask,
            indexVariant,
            indexCondition,
            'critical_value',
            value as string
          );
        } else {
          tasksFormStore.setConditionMetric(indexTask, indexCondition, 'critical_value', value as string);
        }
      },
      [isVariant, tasksFormStore, indexTask, indexVariant, indexCondition]
    );

    const value = isVariant
      ? tasksFormStore.getVariantOnTaskCondition(indexTask, indexVariant, indexCondition)?.critical_value
      : tasksFormStore.getConditionMetric(indexTask, indexCondition)?.critical_value;

    return (
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Typography
          variant="body2"
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            position: 'absolute',
            zIndex: 2,
            top: 0,
            bottom: 0,
            color: t.palette.text.secondary,
            pl: 2,
          })}
        >
          Critical value:
        </Typography>
        <TextField
          value={value ?? ''}
          type="number"
          sx={{
            '&': { width: '100%' },
            '& .MuiInputBase-input': { minHeight: 36, maxHeight: 36, px: 2, py: 0 },
          }}
          InputProps={{
            disabled: isRead,
            style: { minHeight: 36, maxHeight: 36, borderRadius: '8px', paddingLeft: 95 },
          }}
          onChange={(e) => {
            handleChangeCriticalValue(e.target.value);
          }}
        />
      </Box>
    );
  }
);

export default CriticalValueField;
